import { useCallback, useEffect, useRef, useState } from 'react'
import { useTecladoCobrindo } from './teclado'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

type Props = {
  /** O que se está escolhendo. É o título visível. */
  titulo: string
  /**
   * O nome do diálogo para leitor de tela.
   *
   * Separado do título porque o visível é um substantivo ("Família") e o
   * anunciado precisa dizer o que a tela FAZ ("Escolher a família") — quem
   * navega por diálogos ouve o nome sem ver o contexto em volta.
   */
  rotulo: string
  /** O que o campo de busca anuncia. */
  rotuloDaBusca: string
  busca: string
  onBuscar: (texto: string) => void
  /** A lista. Recebe o `<ul>` pronto de quem chama. */
  children: ReactNode
  /**
   * O botão de criar da barra fixa de baixo.
   *
   * A CASCA desenha o botão, e não quem chama, por causa do campo: com a busca
   * vazia não há nome para dar ao item novo, e o certo é levar ao campo — que é
   * da casca. Passar o foco para fora daria um render prop lendo o `ref` durante
   * o render, que é o que `react-hooks/refs` proíbe (e proíbe com razão).
   *
   * `aoCriar: null` quer dizer "não há o que criar agora": o toque foca a busca.
   */
  criar?: { rotulo: string; aoCriar: (() => void) | null }
  /**
   * O texto do campo vazio. "Buscar ou criar…" por padrão, que é o que os
   * seletores de categoria e família fazem — e mentiria num seletor sem `criar`.
   */
  placeholder?: string
  onFechar: () => void
}

/**
 * A casca de um painel de escolha em tela cheia: cabeçalho, busca, lista e uma
 * barra fixa embaixo.
 *
 * ## Ela existe porque o segundo painel ia nascer copiado
 *
 * `EscolherFamilia` resolveu, no lugar dela, um punhado de coisas que não têm
 * nada a ver com famílias: o portal, o Escape, o foco que entra no campo, a
 * área que rola entre dois blocos fixos. Escrever o de categoria por cima
 * daria dois paineis com dois comportamentos de Escape e dois pisos de fonte —
 * e o que ninguém tocasse ficaria para trás em silêncio, que é o argumento da
 * decisão 230 quando a folha de baixo foi extraída do menu de item.
 *
 * ## Portal, e isso NÃO é preferência
 *
 * `position: fixed` não é relativo à janela quando um ancestral tem `filter`,
 * `transform` ou **`backdrop-filter`** — esse ancestral vira o bloco de
 * contenção. Os gatilhos moram dentro de cartões de vidro (`Glass` com
 * `backdrop-filter: blur(18px)`), e sem o portal o `inset-0` mede o CARTÃO: a
 * "tela cheia" abre como uma tira presa ao cabeçalho. Medido no navegador,
 * decisão 242(e).
 *
 * ## Ela sobe junto com o teclado
 *
 * O foco é automático, então **o teclado abre com o painel** — e a barra de
 * baixo, presa ao fundo de um `fixed inset-0`, ficava atrás dele. No iPhone o
 * botão de criar sumia justamente enquanto se digitava o nome do que se quer
 * criar, que é o único momento em que ele serve.
 *
 * O `bottom` sai da `visualViewport` (ver `useTecladoCobrindo`) e vai por
 * `style`: `inset-0` é uma utility, e inline é o que vence sem `!important`.
 * O painel inteiro encolhe, e não só a barra — assim a última linha da lista
 * continua alcançável em vez de ficar debaixo do teclado.
 *
 * ## O campo tem 16px, e o foco é automático
 *
 * Abaixo de 16px o Safari do iPhone dá zoom ao focar — e aqui o foco acontece
 * sozinho na abertura, então a tela inteira saltaria antes de a pessoa tocar em
 * nada. O piso está em `app.css`, fora de `@layer`, e este campo o respeita de
 * propósito em vez de usar `text-sm`.
 *
 * ## As setas andam pela lista, e o Enter escolhe o destacado
 *
 * No desktop, o caminho mais rápido é digitar "mer", ver Mercado no topo e
 * apertar Enter — sem tirar a mão do teclado para mirar o mouse. Por isso o
 * **primeiro item já vem destacado**, ↑↓ trocam o destaque e o Enter clica
 * nele. O foco não sai da busca: continuar digitando continua filtrando.
 *
 * **A casca não conhece os itens** — quem chama passa a `<ul>` pronta —, então
 * as opções são achadas no DOM, na hora da tecla, como `li > button`. As duas
 * listas que existem desenham exatamente isso, um botão por `<li>`, sem botão
 * secundário que pudesse ser confundido com uma opção. Ler o DOM acontece no
 * handler e num efeito, nunca durante o render, que é o que `react-hooks/refs`
 * proíbe.
 *
 * **Digitar volta o destaque para o primeiro**, porque a lista mudou e o índice
 * antigo apontaria para outra coisa. Isso mora no `onChange`, e não num efeito
 * que observe a busca: `setState` dentro de efeito é o que o lint recusa, e com
 * razão.
 *
 * **Enter com a lista vazia não cria nada.** O botão de criar está logo ali, e
 * criar uma categoria por Enter com uma letra trocada seria fácil demais.
 *
 * O contorno do destaque só aparece com ponteiro fino (`app.css`): no celular
 * ninguém usa seta, e um contorno fixo no primeiro item seria ruído.
 */
export function EscolhaEmTelaCheia({
  titulo,
  rotulo,
  rotuloDaBusca,
  busca,
  onBuscar,
  children,
  criar,
  placeholder = 'Buscar ou criar…',
  onFechar,
}: Props) {
  const campo = useRef<HTMLInputElement>(null)
  const lista = useRef<HTMLDivElement>(null)
  const [ativa, setAtiva] = useState(0)
  // **O índice que os handlers leem e escrevem mora num ref**, e o estado só
  // existe para pintar. A primeira versão lia `ativa` da closure do último
  // render: duas teclas antes de o React redesenhar calculavam o mesmo
  // "próximo", e dois ↓ andavam uma casa — visto no navegador. Escrever ref
  // dentro de handler é permitido; o que `react-hooks/refs` proíbe é no render.
  const ativaAgora = useRef(0)
  const tecladoCobrindo = useTecladoCobrindo()

  function destacar(indice: number) {
    ativaAgora.current = indice
    setAtiva(indice)
  }

  // Pinta o destaque depois de cada render — sem lista de dependências de
  // propósito: quem muda a lista é `children`, que é novo a cada render de quem
  // chama. É um atributo, e não uma classe, porque o React não mexe em atributo
  // que ele não escreveu; uma classe acrescentada aqui seria apagada no render
  // seguinte de uma linha que troque o próprio `className`.
  useEffect(() => {
    const todas = opcoesDe(lista.current)
    const indice = Math.min(ativa, todas.length - 1)
    todas.forEach((botao, i) => {
      if (i === indice) botao.setAttribute('data-ativa', 'true')
      else botao.removeAttribute('data-ativa')
    })
  })

  // `useCallback`, e não uma seta no JSX: ler `campo.current` dentro do render
  // é o que o `react-hooks/refs` proíbe — a leitura tem de acontecer quando o
  // botão for tocado, não quando a tela se desenha.
  const focarBusca = useCallback(() => {
    campo.current?.focus()
  }, [])

  // Quem abre isto veio escolher, e digitar é o caminho mais rápido quando a
  // casa tem muitos itens.
  useEffect(() => {
    campo.current?.focus()
  }, [])

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') onFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [onFechar])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={rotulo}
      className="fixed inset-0 z-50 flex flex-col bg-[var(--bg)]"
      style={{ bottom: tecladoCobrindo }}
    >
      <div className="border-b border-[var(--border)] bg-[var(--surface)] p-3 pt-[max(12px,env(safe-area-inset-top))]">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="titulo flex-1 text-base">{titulo}</h2>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="alvo rounded-full border border-[var(--border)] text-sm"
          >
            ✕
          </button>
        </div>

        <input
          ref={campo}
          value={busca}
          onChange={(e) => {
            destacar(0)
            onBuscar(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return
            const todas = opcoesDe(lista.current)
            if (todas.length === 0) return
            // Sem isto, ↑↓ levam o cursor para o começo e o fim do texto.
            e.preventDefault()
            const atual = Math.min(ativaAgora.current, todas.length - 1)
            if (e.key === 'Enter') {
              todas[atual]?.click()
              return
            }
            const proxima =
              e.key === 'ArrowDown' ? Math.min(atual + 1, todas.length - 1) : Math.max(atual - 1, 0)
            destacar(proxima)
            const alvo = todas[proxima]
            if (alvo !== undefined) trazerParaVista(alvo)
          }}
          aria-label={rotuloDaBusca}
          placeholder={placeholder}
          className="w-full rounded-[var(--radius-md)] border border-[var(--border-glass)] bg-transparent px-3 py-2.5 text-base"
        />
      </div>

      <div ref={lista} data-escolha-lista className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {children}
      </div>

      {criar !== undefined && (
        <div className="border-t border-[var(--border)] bg-[var(--surface)] p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={criar.aoCriar ?? focarBusca}
            className="alvo w-full rounded-full border border-[var(--border-glass)] px-4 text-sm text-[var(--brand)]"
          >
            {criar.rotulo}
          </button>
        </div>
      )}
    </div>,
    document.body,
  )
}

/** As opções da lista, na ordem em que aparecem — ver "As setas" acima. */
function opcoesDe(lista: HTMLElement | null): HTMLButtonElement[] {
  if (lista === null) return []
  return [...lista.querySelectorAll<HTMLButtonElement>('li > button:not([disabled])')]
}

/**
 * Rola até a opção destacada, quando ela sai da área visível.
 *
 * O jsdom não implementa `scrollIntoView`, e todo navegador implementa. O tipo
 * `Partial` é o que deixa a checagem existir sem o lint chamá-la de
 * desnecessária.
 */
function trazerParaVista(el: HTMLElement) {
  const rolar = (el as Partial<Pick<HTMLElement, 'scrollIntoView'>>).scrollIntoView
  if (rolar !== undefined) rolar.call(el, { block: 'nearest' })
}
